import { formatPedigreeType, useRouter } from '@application/utils';
import type { ApplicationGetSuggestedBreedersQuery } from '@gql/graphql';
import type { ApplicationsSuggestionRowItemData } from '@presentation/components';
import { PLACEHOLDER_IMAGE_USER } from '@presentation/constants';
import { useTranslation } from '@presentation/utils';
import { useCallback } from 'react';
import { SUGGESTED_BREEDERS_FOR_APPLICATIONS_LIMIT } from './useGetSuggestedBreeders';

export const useFormatBreederSuggestions = (breedIds: number[]) => {
  const { routeLocationDescriptor } = useRouter();
  const { translate } = useTranslation();

  const formatBreederSuggestions = useCallback(
    (breeders: ApplicationGetSuggestedBreedersQuery['findManyDogBreeder']) => {
      if (breedIds.length === 0 || !breeders) {
        return [];
      }

      const newBreeders: ApplicationsSuggestionRowItemData[] = [];
      // We want to show a new row for every breeder's breed, sorted by the breed we are applying for
      // Index 0 is the the breed id we are appying for
      // Does not matter in MT
      breeders.forEach((breeder) => {
        // Copy the array first otherwise we will get an assign error
        breeder.breederBreeds.forEach((breederBreed) => {
          if (!breedIds.includes(breederBreed.dogBreed.id)) {
            return;
          }

          newBreeders.push({
            id: breeder.id,
            name: translate('common.sections.applicationsSuggestion.table.breederLink'),
            link: routeLocationDescriptor('breeder', {
              params: [breeder.translations[0].url ?? ''],
            }).pathname,
            image: breeder.imgs[0]?.small || PLACEHOLDER_IMAGE_USER,
            lastConnectedBadgeType: breeder.lastConnectedType ?? undefined,
            breedId: breederBreed.dogBreed.id,
            breedName: breederBreed.dogBreed.translations[0].name as string,
            pedigree: formatPedigreeType(translate, breeder.dogsPedigree),
          });
        });
      });

      return newBreeders
        .sort((a, b) => {
          if (a.breedId === breedIds[0] && b.breedId === breedIds[0]) {
            return 0;
          }

          if (a.breedId === breedIds[0]) {
            return -1;
          }

          if (b.breedId === breedIds[0]) {
            return 1;
          }

          return 0;
        })
        .slice(0, SUGGESTED_BREEDERS_FOR_APPLICATIONS_LIMIT);
    },
    [breedIds, routeLocationDescriptor, translate],
  );

  return { formatBreederSuggestions };
};
